<script setup lang="ts">
import { defineRule, useForm } from 'vee-validate';
import { required } from '@vee-validate/rules';

// components
import CiModalFooter from '@/components/CiModal/CiModalFooter.vue';
import CiModalHeader from '@/components/CiModal/CiModalHeader.vue';

// validation
defineRule('required', required);

/********************
 * PROPS & EMITS     *
 ********************/
export interface CiModalUserprofileIncompleteProps {
  backOnCancel: boolean;
  closeable?: boolean;
  fields: {
    label: string;
    placeholder: string;
    required: boolean;
    type: string;
    name: string;
  }[];
  text?: string | null;
  title?: string | null;
}
const props = withDefaults(defineProps<CiModalUserprofileIncompleteProps>(), {
  closeable: true,
  text: null,
  title: null,
});

const emit = defineEmits<{
  close: [];
}>();

/********************
 * COMPOSITIONS      *
 ********************/
const router = useRouter();
const userService = useUserService();
const userStore = useUserStore();
const { $gettext } = useGettext();
const { locale } = useI18n();

/********************
 * REFS & VARS       *
 ********************/
const isSubmitting = ref(false);
const showErrorMessage = ref(false);
const showSuccessMessage = ref(false);

const i18nButtonLabel = computed(() => {
  if (showSuccessMessage.value) {
    return $gettext('Close');
  }

  if (showErrorMessage.value) {
    return $gettext('Back');
  }

  return $gettext('Submit');
});

const errorMessage = computed(() => {
  return $gettext('Something went wrong. Please try again.');
});

const successMessage = computed(() => {
  return $gettext('Your data has been updated correctly.');
});

// form
const fieldModels = reactive({});
const { validate, errors, defineField } = useForm({
  validationSchema: props.fields.reduce((acc, cur) => {
    acc[cur.name] = cur.required ? 'required' : '';
    return acc;
  }, {}),
});

// dynamically generate field models and push to reactive object
props.fields.forEach((key) => {
  const [name] = defineField(key.name);
  fieldModels[key.name] = name;
});

/********************
 * FUNCTIONS         *
 ********************/
async function submitForm() {
  const { valid } = await validate();
  if (!valid) {
    return false;
  }
  const user = { ...fieldModels };

  if (!user.email) {
    user.email = userStore.user.profile?.email;
  }

  isSubmitting.value = true;

  userService
    .update(locale.value, user)
    .then((response) => {
      isSubmitting.value = false;
      showSuccessMessage.value = true;
      showErrorMessage.value = false;
      userStore.user.profile = response;
    })
    .catch(() => {
      isSubmitting.value = false;
      showSuccessMessage.value = false;
      showErrorMessage.value = true;
    });
}

function onClickBack() {
  if (props.backOnCancel) {
    router.go(-1);
  }
}
</script>

<template>
  <div class="rounded bg-white shadow-2xl">
    <form
      ref="form"
      novalidate
      @submit.prevent="submitForm"
    >
      <CiModalHeader
        :title="title"
        :closeable="closeable"
        @modal-close="closeable ? emit('close') : false"
      />
      <div class="container-fluid mt-4 p-4">
        <template v-if="!showSuccessMessage && !showErrorMessage">
          <p v-if="text">
            {{ text }}
          </p>
          <template
            v-for="field in props.fields"
            :key="field.name"
          >
            <InputTextfield
              :id="field.name"
              v-model="fieldModels[field.name]"
              :type="field.type"
              :label="field.label"
              :error="!!errors[field.name]"
              :placeholder="field.placeholder"
              :name="field.name"
              :required="field.required"
              inverted
            />
          </template>
        </template>

        <template v-if="showSuccessMessage && !showErrorMessage">
          <CiSuccess :message="successMessage" />
        </template>

        <template v-if="showErrorMessage && !showSuccessMessage">
          <CiError :message="errorMessage" />
        </template>
      </div>
      <CiModalFooter class="p-4">
        <div class="container-fluid">
          <div class="flex justify-center">
            <CiButton
              v-if="!showSuccessMessage && !showErrorMessage"
              tag="button"
              variant="text"
              class="mr-2"
              @click="onClickBack"
            >
              {{ $gettext('Cancel') }}
            </CiButton>
            <CiButton
              v-if="!showSuccessMessage && !showErrorMessage"
              tag="button"
              :disabled="isSubmitting"
              @click="submitForm"
            >
              {{ i18nButtonLabel }}
            </CiButton>
            <CiButton
              v-if="showSuccessMessage"
              tag="button"
              @click="emit('close')"
            >
              {{ i18nButtonLabel }}
            </CiButton>
            <CiButton
              v-if="showErrorMessage"
              tag="button"
              @click="showErrorMessage = false"
            >
              {{ i18nButtonLabel }}
            </CiButton>
          </div>
        </div>
      </CiModalFooter>
    </form>
  </div>
</template>
